import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CleanInputIcon from 'components/Form/shared/CleanInputIcon';
import Conditional from 'components/Conditional';
import { Input } from '../Input';
import MagnifyingIcon from 'assets/icons/magnifying.svg';
import {
  StyledSelectWrapper,
  StyledSelect,
  StyledSearchBar,
  StyledDropdownContent,
  StyledDropdownItem,
  DropdownOption,
  StyledError,
  StyledDropdownContentWrapper,
} from './styled';

const Select = forwardRef((props, ref) => {
  const {
    size,
    placeholder,
    isActive = true,
    onChange,
    onSelect = () => {},
    onInputClear = () => {},
    value,
    isRequired = false,
    options = [],
    isSearchDisabled = false,
    className = '',
    error,
    errormessage,
    disabled,
    children,
    'data-cy': dataCy,
    cleanFields,
    name,
    inputDifferent = false,
    inputWidth,
    onInputChange,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [filterWord, setFilterWord] = useState('');
  const [selectedValue, setSelectedValue] = useState(value?.value);
  const [selectedLabel, setSelectedLabel] = useState('');
  const selectRef = useRef(ref);
  const [newOptions, setNewOptions] = useState([]);

  useEffect(() => {
    if (options.length === 0) {
      setNewOptions([value]);
    } else {
      setNewOptions(options);
    }
  }, [value, options]);

  const handleValueChange = useCallback(
    valueToFind => {
      if (valueToFind) {
        const current = newOptions.find(option => option?.value === valueToFind);

        if (current) {
          setSelectedValue(current?.value);
          setSelectedLabel(current?.label);
        }
      } else {
        setSelectedValue('');
        setSelectedLabel('');
      }
    },
    [newOptions],
  );

  useEffect(() => {
    handleValueChange(value?.value || value);
  }, [handleValueChange, value]);

  const handleChange = option => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    onChange && onChange(option.value);
  };

  const openDropDownContent = () => {
    setIsOpen(() => !isOpen);
  };

  const filteredOptions = useMemo(() => {
    const filterOptionsByWord = option => {
      if (!filterWord) {
        return option;
      }

      const wordMatches = `${option.label}`.toLowerCase().includes(filterWord.toLowerCase());

      if (wordMatches) {
        return option;
      }

      return null;
    };
    return newOptions.filter(option => filterOptionsByWord(option));
  }, [newOptions, filterWord]);

  const handleClickOutside = useCallback(
    e => {
      if (selectRef.current.contains(e.target)) {
        // inside click
      } else {
        // outside click
        setIsOpen(false);
      }
    },
    [selectRef],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    setFilterWord('');
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  const handleKeyPress = e => {
    if (e.key === 'Enter' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      handleChange(filteredOptions[0]);
      setIsOpen(false);
    }
  };

  return (
    <StyledSelectWrapper
      isActive={isActive}
      onKeyPress={handleKeyPress}
      className={className}
      ref={selectRef}
      inputSize={size}
    >
      <StyledSelect
        readOnly
        type="text"
        placeholder={placeholder}
        size={size}
        error={error}
        value={selectedLabel}
        disabled={disabled}
        onClick={openDropDownContent}
        data-cy={`toggle-${dataCy}`}
        inputDifferent={inputDifferent}
        inputWidth={inputWidth}
      />
      <Conditional when={!disabled && !isRequired && selectedValue !== '' && newOptions?.length > 0}>
        <CleanInputIcon
          size={size}
          onClick={() => {
            handleChange({ value: '', label: '' });
            if (name === 'unitGroup') {
              cleanFields();
            }
            onInputClear();
          }}
        />
      </Conditional>
      <StyledDropdownContent isOpen={isOpen}>
        {isSearchDisabled !== true && (
          <StyledSearchBar inputDifferent={inputDifferent}>
            <Input
              type="text"
              autoComplete="off"
              name="search"
              size="extraSmall"
              placeholder="Pesquisar"
              icon={MagnifyingIcon}
              data-cy={`search-${dataCy}`}
              ref={selectRef}
              onChange={e => {
                onInputChange && onInputChange(e.target.value);
                setFilterWord(e.target.value);
              }}
            />
          </StyledSearchBar>
        )}
        <StyledDropdownContentWrapper>
          {filteredOptions.map(option => (
            <StyledDropdownItem
              key={option.value}
              data-cy={`${dataCy}-option`}
              onClick={() => {
                setIsOpen(false);
                handleChange(option);
                onSelect(option);
              }}
              inputDifferent={inputDifferent}
            >
              <DropdownOption />
              <label>{option.label}</label>
            </StyledDropdownItem>
          ))}
          {children}
        </StyledDropdownContentWrapper>
      </StyledDropdownContent>
      <StyledError>
        <p>{error && errormessage}</p>
      </StyledError>
    </StyledSelectWrapper>
  );
});

Select.propTypes = {
  size: PropTypes.string,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
  value: PropTypes.any,
  options: PropTypes.array,
  defaultValue: PropTypes.any,
  isClearInputActive: PropTypes.bool,
  inputDifferent: PropTypes.bool,
  inputWidth: PropTypes.string,
};

Select.defaultProps = {
  size: 'medium',
  options: [],
  defaultValue: { value: '', label: '' },
};

export { Select };
